import { isValid } from "date-fns";
import i18n from "i18next";
import { z } from "zod";

import type { ExtendedProperty } from "@/features/payment-methods/hooks/use-available-payment-methods";

import { isCardExpiryValid } from "../helpers/card-expiry";

export class SchemaGenerator {
  static generateFieldSchema(field: ExtendedProperty): z.ZodTypeAny {
    const fieldName = field.title || field.property_name;

    if (field.client_type === "year-month") {
      const schema = z.string().trim();
      return field.required
        ? schema.min(1, { message: `${field.title} is required` })
        : schema.optional();
    }

    if (field.client_type === "numeric") {
      const numberSchema = z.string().regex(/^\d*$/, {
        message: i18n.t("invalid_field_error", { "0": fieldName }),
      });
      return field.required
        ? numberSchema.min(1, {
            message: `${field.title} is required`,
          })
        : numberSchema.optional();
    }

    if (field.client_type === "phone") {
      let phoneSchema = z.string().trim();

      if (field.pattern) {
        phoneSchema = phoneSchema.regex(new RegExp(field.pattern), {
          message: i18n.t("invalid_field_error", { "0": fieldName }),
        });
      }

      if (field.minLength && field.maxLength) {
        phoneSchema = phoneSchema.min(field.minLength).max(field.maxLength, {
          message: i18n.t("error_valueBetweenMinMax", {
            "0": fieldName,
            "1": field.minLength,
            "2": field.maxLength,
          }),
        });
      } else if (field.minLength) {
        phoneSchema = phoneSchema.min(field.minLength, {
          message: i18n.t("error_valueExactLength", {
            "0": fieldName,
            "1": field.minLength,
          }),
        });
      } else if (field.maxLength) {
        phoneSchema = phoneSchema.max(field.maxLength, {
          message: i18n.t("error_valueExceedsMax", {
            "0": fieldName,
            "1": field.maxLength,
          }),
        });
      }

      return field.required
        ? phoneSchema.min(1, {
            message: `${field.title} is required`,
          })
        : phoneSchema.optional();
    }

    if (field.client_type === "date") {
      if (field.required) {
        return z
          .string()
          .min(1, { message: `${field.title} is required` })
          .refine((date) => isValid(new Date(date)), {
            message: `${field.title} is not a valid date`,
          });
      }

      return z
        .string()
        .refine((date) => !date || isValid(new Date(date)), {
          message: `${field.title} is not a valid date`,
        })
        .optional();
    }

    if (!field.required && field.minLength && field.maxLength) {
      return z
        .string()
        .trim()
        .refine(
          (val) => {
            if (!val) return true;
            return (
              val.length >= field.minLength! && val.length <= field.maxLength!
            );
          },
          {
            message: i18n.t("error_valueBetweenMinMax", {
              "0": fieldName,
              "1": field.minLength,
              "2": field.maxLength,
            }),
          }
        );
    }

    // TODO: Missing translations for form labels and messages
    let stringSchema = z.string().trim();

    if (field.client_type === "email") {
      stringSchema = stringSchema.email(i18n.t("error_email"));
    }

    if (field.minLength || field.maxLength) {
      let lengthSchema = stringSchema;

      if (
        field.minLength &&
        field.maxLength &&
        field.minLength === field.maxLength
      ) {
        lengthSchema = lengthSchema.length(field.minLength, {
          message: i18n.t("error_valueExactLength", {
            "0": fieldName,
            "1": field.minLength,
          }),
        });
      } else if (field.minLength && field.maxLength) {
        lengthSchema = lengthSchema.min(field.minLength).max(field.maxLength, {
          message: i18n.t("error_valueBetweenMinMax", {
            "0": fieldName,
            "1": field.minLength,
            "2": field.maxLength,
          }),
        });
      } else if (field.maxLength) {
        lengthSchema = lengthSchema.max(field.maxLength, {
          message: i18n.t("error_valueExceedsMax", {
            "0": fieldName,
            "1": field.maxLength,
          }),
        });
      }

      stringSchema = lengthSchema;
    }

    if (field.pattern) {
      const pattern = new RegExp(field.pattern);
      if (!field.required) {
        return stringSchema
          .refine((val) => !val || pattern.test(val), {
            message: i18n.t("invalid_field_error", { "0": fieldName }),
          })
          .optional();
      }
      stringSchema = stringSchema.regex(pattern, {
        message: i18n.t("invalid_field_error", { "0": fieldName }),
      });
    }

    if (field.required && !field.minLength) {
      return stringSchema.min(1, {
        message: `${field.title} is required`,
      });
    }

    return stringSchema.optional();
  }

  static generateCardExpirySchema(field: ExtendedProperty) {
    return z
      .string()
      .min(1, { message: `${field.title} is required` })
      .refine((expiry) => isCardExpiryValid(expiry), {
        message: i18n.t("invalid_expiration_date"),
      });
  }

  static generateFormSchema(fields: ExtendedProperty[]) {
    const shape: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
      shape[field.property_name] = this.generateFieldSchema(field);
    });

    return z.object(shape);
  }
}
